import { AlertTriangle } from 'lucide-preact'
import type { EmailBlastSplitTestSkipDto } from '@/types/emailBlast'

/**
 * ORB-472: the touchpoint asked for a split test and this blast sent the control to everyone instead.
 * Degrading is correct — the real audience only exists once the send is staged — but it used to happen
 * silently, so a user who configured an A/B test just saw a normal send. This is the missing "why".
 *
 * Stays VISIBLE rather than moving into the Rollout log with the rest of the A/B material (ORB-706):
 * "the test you configured did not run" is a configuration failure, and folding it away would put it
 * back where ORB-472 found it.
 */
export function SplitTestSkippedPanel({ skip }: { skip: EmailBlastSplitTestSkipDto }) {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="space-y-1">
        <p className="font-medium">
          Split test skipped — all {skip.EligibleCount.toLocaleString()} recipients get subject A, the control.
        </p>
        <p>{describeSplitTestSkip(skip)}</p>
      </div>
    </div>
  )
}

/** Turns a skip record into the sentence that explains it, using the numbers the skip was decided on. */
function describeSplitTestSkip(skip: EmailBlastSplitTestSkipDto): string {
  const cohort = skip.CohortSize.toLocaleString()
  const eligible = skip.EligibleCount.toLocaleString()
  switch (skip.Reason) {
    case 'BelowPerVariantFloor':
      return (
        `Only ${cohort} of ${eligible} eligible recipients fell into the test cohort, but each of the ` +
        `${skip.VariantCount} subject lines needs at least ${skip.RequiredPerVariant.toLocaleString()} — ` +
        `${(skip.VariantCount * skip.RequiredPerVariant).toLocaleString()} between them. ` +
        'A bigger list or a larger cohort percentage would clear it.'
      )
    case 'NoChallengerSubject':
      return (
        'The email copy had only one subject line when this send was staged, so there was nothing to test ' +
        'the control against. Add a second subject in the copy builder before the next send.'
      )
    case 'NoRemainder':
      return (
        `The test cohort covered the whole audience (${cohort} of ${eligible} eligible recipients), leaving ` +
        'nobody for the winning subject to be sent to. Lower the cohort percentage.'
      )
    case 'RecipientLocal':
      return (
        "This send goes out at each recipient's own local time, so there is no shared window in which to " +
        'compare the subject lines. Send at a fixed time to run a test.'
      )
    default:
      // Unreachable via the API (it rejects an unrecognized reason on read) — but a render throw blanks
      // the whole page, so name the raw value instead of hiding it.
      return `The test could not run (${String(skip.Reason)}).`
  }
}
